import { GamepadType } from '../types/Gamepad';

// The d-pad "undo rating" gesture is surfaced as a synthetic button event so
// consumers can react to a single logical `UNDO` universal button; on the
// flashcard screen this undoes the last flashcard rating. Xbox/PS controllers
// report the d-pad as real buttons (D-pad Left = index 14, standard mapping),
// so we reuse that index for the synthetic 8BitDo axis-based event too; the two
// controller families never emit events simultaneously, and sharing the index
// keeps the per-button debounce in the flashcard consumer coherent.
export const UNDO_SYNTH_BUTTON_INDEX = 14;

// D-pad directions read as an axis pushed toward -1 (up and left are negative on
// the standard gamepad axes). Hysteresis: engage past ENGAGE, re-arm below RELEASE
// so a held d-pad fires exactly once per press instead of every polled frame.
const UNDO_ENGAGE_THRESHOLD = -0.5;
const UNDO_RELEASE_THRESHOLD = -0.3;

/**
 * For 8BitDo controllers the d-pad is reported as axes (not buttons), and one
 * axis is already reserved for scrolling. The remaining axis carries "undo":
 * - 8bitVertical: axis 0 (left/right) scrolls → undo is UP on axis 1.
 * - 8bitHorizontal: axis 1 (up/down) scrolls → undo is LEFT on axis 0.
 * Xbox/PS/unknown return null — their d-pad arrives as button index 14 instead.
 */
export function resolveUndoAxisIndex(
  gamepadType: GamepadType | null
): number | null {
  if (gamepadType === '8bitVertical') return 1;
  if (gamepadType === '8bitHorizontal') return 0;
  return null;
}

/**
 * Edge-detect an undo press on a d-pad axis. Given the current axis value and
 * whether the axis is already engaged, returns whether to fire undo now and the
 * next engaged state to persist.
 */
export function evaluateUndoAxis(
  value: number,
  engaged: boolean
): { trigger: boolean; engaged: boolean } {
  if (!engaged && value < UNDO_ENGAGE_THRESHOLD) {
    return { trigger: true, engaged: true };
  }
  if (engaged && value > UNDO_RELEASE_THRESHOLD) {
    return { trigger: false, engaged: false };
  }
  return { trigger: false, engaged };
}
